import { useState } from 'react';
import { FormControl } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconSearch } from 'src/assets/icons/Search';
import { CustomInput } from 'src/components/CustomInput';
import { TruncatedTypography } from 'src/components/TruncatedTypography';

import { ErrorMessage } from '../ErrorMessage';

import { AddResourceMenuItem } from './components/AddResourceMenuItem';
import { ResourceMenuItem } from './components/ResourceMenuItem';
import {
  OPTION_ADD_RESOURCE_ID,
  OPTION_ADD_RESOURCE_LABEL,
  OPTION_INPUT_VALUE_ID,
  SELECTED_OPTION_KEY,
} from './const';
import { useStyles } from './useStyles';

const OPTION_ADD_RESOURCE = {
  id: OPTION_ADD_RESOURCE_ID,
  name: OPTION_ADD_RESOURCE_LABEL,
};

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  placeholder: PropTypes.string,
  inputValue: PropTypes.string,
  resources: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedResource: PropTypes.shape(),
  onAddResource: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  hideAddOption: PropTypes.bool,
  CustomPopper: PropTypes.elementType,
  showInputValueAsOption: PropTypes.bool,
  showSearchIcon: PropTypes.bool,
  selectedOptionKey: PropTypes.string,
};
const defaultProps = {
  className: '',
  classes: {},
  placeholder: '',
  inputValue: null,
  selectedResource: null,
  onInputChange: null,
  error: null,
  disabled: false,
  hideAddOption: false,
  CustomPopper: undefined,
  showInputValueAsOption: false,
  showSearchIcon: false,
  selectedOptionKey: SELECTED_OPTION_KEY.name,
};

export const SelectResourceFileOrUrl = ({
  className,
  classes,
  placeholder,
  inputValue,
  resources,
  selectedResource,
  onAddResource,
  onSelect,
  onInputChange,
  error,
  disabled,
  hideAddOption,
  CustomPopper,
  showInputValueAsOption,
  showSearchIcon,
  selectedOptionKey,
}) => {
  const styles = useStyles({ classes });

  const [searchValue, setSearchValue] = useState('');
  const localInputValue = inputValue != null ? inputValue : searchValue;
  const combinedResources = [OPTION_ADD_RESOURCE, ...resources];

  const handleInputChange = (event, newValue, reason) => {
    // Do not change the input value when the user selects "Add resource" option
    // TODO: comparing by name is not the best way to check if the option is "Add resource", as we might have a resource with the same name
    if (newValue === OPTION_ADD_RESOURCE_LABEL) {
      return;
    }

    // This handler also gets called when the "value" prop is cleared, we don't want to clear the "input value" in this case
    if (reason === 'reset' && !newValue) {
      return;
    }

    // If user starts typing again when we have a "resource" selected - clear the selection
    if (reason === 'input' && selectedResource && selectedResource.name !== newValue) {
      onSelect(null);
    }

    setSearchValue(newValue);
    onInputChange?.(newValue);
  };

  const handleChange = (event, selected, reason) => {
    if (selected?.id === OPTION_ADD_RESOURCE_ID) {
      onAddResource();
      return;
    }

    if (
      showInputValueAsOption &&
      reason === 'select-option' &&
      selected?.id === OPTION_INPUT_VALUE_ID
    ) {
      onSelect({ url: selected.name });
      return;
    }

    if (reason === 'create-option' && typeof selected === 'string') {
      onSelect({ url: selected });
      return;
    }

    if (selected && reason === 'select-option') {
      onSelect({ resource: selected });
      return;
    }

    onSelect(null);
  };

  const filterOptions = (options = [], { inputValue = '' } = {}) => {
    const filtered = options.filter((option) =>
      option.name.toLowerCase().includes(inputValue.toLowerCase())
    );

    if (showInputValueAsOption && inputValue !== '') {
      const isExistingOption = options.some(
        (option) => option.name.toLowerCase() === inputValue.toLowerCase()
      );
      if (!isExistingOption) {
        filtered.unshift({ id: OPTION_INPUT_VALUE_ID, name: inputValue });
      }
    }

    return filtered;
  };

  const renderOptionContent = (option) => {
    if (option.id === OPTION_ADD_RESOURCE_ID) {
      return <AddResourceMenuItem option={option} />;
    }
    if (option.id === OPTION_INPUT_VALUE_ID) {
      return <TruncatedTypography>{option.name}</TruncatedTypography>;
    }
    return (
      <ResourceMenuItem
        classes={{ name: styles.resourceMenuItemName }}
        option={option}
      />
    );
  };

  return (
    <FormControl
      className={cx(styles.root, className)}
      disabled={disabled}
    >
      <Autocomplete
        className={styles.autocomplete}
        PopperComponent={CustomPopper}
        options={hideAddOption ? resources : combinedResources}
        filterOptions={filterOptions}
        getOptionLabel={(option) => option?.[selectedOptionKey] || ''}
        renderOption={renderOptionContent}
        inputValue={localInputValue || ''}
        value={selectedResource}
        onInputChange={handleInputChange}
        onChange={handleChange}
        renderInput={(params) => (
          <CustomInput
            {...params.InputProps}
            className={styles.input}
            classes={{ inputAdornedEnd: styles.inputAdornedEnd }}
            inputProps={params.inputProps}
            data-testid="selectResourceFileOrUrlInput"
            placeholder={placeholder || 'Enter URL or file name'}
            startAdornment={showSearchIcon && <IconSearch className={styles.searchIcon} />}
          />
        )}
        disabled={disabled}
        disableClearable
        freeSolo
      />

      <ErrorMessage error={error} />
    </FormControl>
  );
};
SelectResourceFileOrUrl.displayName = 'SelectResourceFileOrUrl';
SelectResourceFileOrUrl.propTypes = propTypes;
SelectResourceFileOrUrl.defaultProps = defaultProps;
